/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { css } from '@emotion/react'
import { App, DatePicker, Tooltip } from 'antd'
import dayjs from 'dayjs'
import type { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)
import colors from '@sportninja/common/constants/appColors'
import type { Venue } from '../hooks/useSchedulerLocations'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import type { EvaluationResult, GameEditData } from '../hooks/useGameEdit'
import type { SchedulerViolation } from '../hooks/useSchedulerGeneration'
import {
  buildInlineGameViolations,
  classifyViolation,
  dedupeViolations,
  humanizeWeekRefs,
  intuitiveMessage,
  isSetupLimitedOutcome,
  outcomeAwareLabel,
  readOutcome,
  relatedGameViolations,
  resolveTeamLabel,
  type InlineViolation,
} from '../utils/violations'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { Competition } from '../hooks/useSchedulerCompetition'
import {
  type HourSlot,
  getTimeRangesForFacilityAndDay,
  splitIntoHourSlots,
  buildEditDiff,
} from '../utils/gameEditHelpers'

export type GameStatus = 'success' | 'warning' | 'error'

export interface GeneratedGame {
  id: string
  /** Per-division sequence (restarts at 1 per division — not flight-unique). */
  gameNumber: number
  /** Flight-unique number rendered as "GAME #"; falls back to gameNumber if null. */
  globalGameNumber: number | null
  date: string
  time: string
  location: string
  locationId?: string
  surface: string
  surfaceId?: string
  divisionId: string
  division: string
  homeTeam: string
  homeTeamId?: string
  awayTeam: string
  awayTeamId?: string
  hasError: boolean
  hasWarning: boolean
  startsAt?: string
  endsAt?: string
  timezone?: string
}

interface EditFormState {
  venueId: string
  facilityId: string
  homeTeamId: string
  awayTeamId: string
  startsAt: string
  endsAt: string
}

interface TeamOption {
  id: string
  name: string
}

interface GeneratedGamesSectionProps {
  games: GeneratedGame[]
  totalGames?: number
  loading?: boolean
  onExport?: () => void
  onViewAll?: () => void
  onSaveGame?: (gameId: string, data: GameEditData) => Promise<boolean>
  onSwapGames?: (gameAId: string, gameBId: string) => void
  editingGameId?: string | null
  onStartEditing?: (gameId: string) => void
  onCancelEditing?: () => void
  saving?: boolean
  editError?: string | null
  onClearError?: () => void
  venues?: Venue[]
  divisions?: DivisionItem[]
  satisfactionScore?: number | null
  lastEvaluation?: EvaluationResult | null
  initialViolations?: SchedulerViolation[]
  /** Resolves `affected_team_uids` → display name for the inline expansion. */
  teamNames?: Record<string, string>
  timeSlots?: TimeSlot[]
  constraints?: SchedulerConstraint[]
  competition?: Competition | null
  filterContent?: React.ReactNode
  onSaveCheckpoint?: () => void
  onRestoreCheckpoint?: () => void
  isSavingCheckpoint?: boolean
  isRestoringCheckpoint?: boolean
  hasChanges?: boolean
  publishedDivisionIds?: Set<string>
  /**
   * Unique game id to scroll into view + flash; paired with `highlightToken`.
   * Keyed by id (not game_number) because game_number repeats across divisions.
   */
  highlightGameId?: string | null
  /** Bumped by the parent on each jump so repeat jumps re-fire the flash. */
  highlightToken?: number
  /** Advisory badge → scroll to the Constraints & Violations panel. */
  onShowAdvisories?: () => void
  /**
   * Strict-first: true when EVERY completed division's solve satisfied all
   * enabled constraints as hard (no strict→soft fallback). Drives the
   * understated "All rules satisfied" success chip — which also requires this
   * component's own visible inline-violation count to be zero.
   */
  constraintsStrictlySatisfied?: boolean
}

// ─── Pure Helpers ─────────────────────────────────────────────────
// getTimeRangesForFacilityAndDay, splitIntoHourSlots, buildEditDiff
// are imported from ../utils/gameEditHelpers
// resolveTeamLabel, intuitiveMessage, outcomeAwareLabel are imported from
// ../utils/violations (pure, unit-tested).

// ─── Inline SVG Icons ────────────────────────────────────────────

const ExpandMoreIcon: React.FC<{
  size?: number
  color?: string
  expanded?: boolean
}> = ({ size = 24, color = colors.NEUTRAL_50, expanded = true }) => {
  const iconStyle = {
    transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
    transition: 'transform 0.2s ease',
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={iconStyle}
    >
      <path d='M7 10L12 15L17 10H7Z' fill={color} />
    </svg>
  )
}

const DownloadIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.PRIMARY,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path d='M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z' fill={color} />
  </svg>
)

const WarningIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.WARNING_200,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 5.99L19.53 19H4.47L12 5.99ZM12 2L1 21H23L12 2ZM13 16H11V18H13V16ZM13 10H11V14H13V10Z'
      fill={color}
    />
  </svg>
)

const SuccessIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.SUCCESS_300,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
      fill={color}
    />
  </svg>
)

const CloseIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 20,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z'
      fill={color}
    />
  </svg>
)

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  margin-top: 24px;
`

const headerRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const sectionTitleContainerStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
`

const sectionTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 14px;
  line-height: 16.8px;
  letter-spacing: 1.68px;
  text-transform: uppercase;
  color: ${colors.WHITE};
  margin: 0;
`

const exportButtonStyles = css`
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 8px;
  background: transparent;
  border: 1px solid ${colors.PRIMARY};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.PRIMARY}20;
  }
`

const exportButtonTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.PRIMARY};
  margin: 0;
`

const tableContainerStyles = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const tableHeaderStyles = css`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 12px 16px;
  background: ${colors.SECONDARY_300};
`

const columnHeaderStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  margin: 0;
  text-transform: uppercase;
`

const sortableHeaderStyles = css`
  ${columnHeaderStyles};
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;

  &:hover {
    color: ${colors.WHITE};
  }
`

const activeSortHeaderStyles = css`
  color: ${colors.WHITE};
`

const SortIndicator: React.FC<{
  active: boolean
  direction: 'asc' | 'desc'
}> = ({ active, direction }) => {
  const indicatorStyle = { flexShrink: 0, opacity: active ? 1 : 0.3 }
  return (
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      style={indicatorStyle}
    >
      {direction === 'asc' ? (
        <path
          d='M6 3L10 8H2L6 3Z'
          fill={active ? colors.PRIMARY : colors.NEUTRAL_70}
        />
      ) : (
        <path
          d='M6 9L2 4H10L6 9Z'
          fill={active ? colors.PRIMARY : colors.NEUTRAL_70}
        />
      )}
    </svg>
  )
}

const getRowStyles = (
  _isEven: boolean,
  isEditing?: boolean,
  isAffected?: boolean,
  isPublished?: boolean,
  isFlashed?: boolean
) => {
  // Affected = a CONFLICT touches this game → error coloring (red).
  const bg = isFlashed
    ? `${colors.PRIMARY}26`
    : isAffected
    ? `${colors.ERROR_200}14`
    : isPublished
    ? `${colors.SUCCESS_300}15`
    : 'transparent'

  const hoverBg = isPublished ? `${colors.SUCCESS_300}22` : colors.SECONDARY_200

  return css`
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 12px 16px;
    background: ${bg};
    cursor: ${isEditing ? 'default' : 'pointer'};
    transition: background 0.4s ease, box-shadow 0.4s ease;
    ${isAffected ? `border-left: 3px solid ${colors.ERROR_200};` : ''}
    ${isFlashed ? `box-shadow: inset 0 0 0 2px ${colors.PRIMARY};` : ''}

    ${!isEditing ? `&:hover { background: ${hoverBg}; }` : ''}
  `
}

const cellTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const statusCellStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 128px;
  flex-shrink: 0;
`

const tooltipIconWrapper = css`
  display: flex;
  align-items: center;
  justify-content: center;
`

const iconBtnStyles = css`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover {
    opacity: 0.8;
    background: ${colors.SECONDARY_200};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    &:hover {
      background: transparent;
    }
  }
`

const inlineSelectStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.WHITE};
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  padding: 6px 8px;
  width: 100%;
  appearance: none;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: ${colors.PRIMARY};
  }

  option {
    background: ${colors.SECONDARY_300};
    color: ${colors.WHITE};
  }
`

const inlineDatePickerStyles = css`
  && {
    font-family: 'Inter', sans-serif;
    font-size: 13px;
    color: ${colors.WHITE};
    background: ${colors.SECONDARY_300};
    border: 1px solid ${colors.SECONDARY_100};
    border-radius: 6px;
    padding: 6px 8px;
    width: 100%;
    cursor: pointer;

    .ant-picker-input > input {
      color: ${colors.WHITE};
      font-size: 13px;
    }

    .ant-picker-suffix {
      color: ${colors.WHITE};
    }

    &:hover,
    &.ant-picker-focused {
      border-color: ${colors.PRIMARY};
    }
  }
`

const editActionBtnStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`

const errorBannerStyles = css`
  padding: 8px 16px;
  background: ${colors.ERROR_300}20;
  border-left: 3px solid ${colors.ERROR_300};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  color: ${colors.ERROR_100};
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const scoreBadgeStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 8px;
`

// ─── Column width definitions ────────────────────────────────────

const colDate = css`
  width: 160px;
  flex-shrink: 0;
`
const colTime = css`
  width: 100px;
  flex-shrink: 0;
`
const colLocation = css`
  width: 150px;
  flex-shrink: 0;
`
const colSurface = css`
  width: 140px;
  flex-shrink: 0;
`
const colFlex = css`
  flex: 1;
  min-width: 0;
`
const colGameNum = css`
  width: 72px;
  flex-shrink: 0;
`

// ─── Component ───────────────────────────────────────────────────

const GeneratedGamesSection: React.FC<GeneratedGamesSectionProps> = ({
  games,
  loading = false,
  onExport,
  onSaveGame,
  onSwapGames,
  editingGameId,
  onStartEditing,
  onCancelEditing,
  saving = false,
  editError,
  onClearError,
  venues = [],
  divisions = [],
  satisfactionScore,
  lastEvaluation,
  initialViolations = [],
  teamNames = {},
  timeSlots = [],
  constraints = [],
  competition,
  filterContent,
  onSaveCheckpoint,
  onRestoreCheckpoint,
  isSavingCheckpoint = false,
  isRestoringCheckpoint = false,
  hasChanges = false,
  publishedDivisionIds,
  highlightGameId,
  highlightToken,
  onShowAdvisories,
  constraintsStrictlySatisfied = false,
}) => {
  const { message } = App.useApp()

  useEffect(() => {
    if (editError) {
      message.error(editError)
      onClearError?.()
    }
  }, [editError, message, onClearError])

  const expanded = true

  // ─── Sort state ─────────────────────────────────────────────────
  type SortKey =
    | 'gameNumber'
    | 'date'
    | 'time'
    | 'homeTeam'
    | 'awayTeam'
    | 'location'
    | 'surface'
    | 'division'
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((prev) => {
      if (prev === key) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
        return key
      }
      setSortDirection('asc')
      return key
    })
  }, [])
  const [expandedViolationGames, setExpandedViolationGames] = useState<
    Set<number>
  >(new Set())

  // ─── Swap state ─────────────────────────────────────────────────
  const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set())

  // Swap eligibility: exactly 2 games selected, same division
  const swapEligible = useMemo(() => {
    if (selectedGameIds.size !== 2 || !onSwapGames) {
      return null
    }
    const selectedArr = Array.from(selectedGameIds)
    const gameA = games.find((g) => g.id === selectedArr[0])
    const gameB = games.find((g) => g.id === selectedArr[1])
    if (!gameA || !gameB) {
      return null
    }
    if (gameA.divisionId !== gameB.divisionId) {
      return null
    }
    return { gameAId: selectedArr[0], gameBId: selectedArr[1] }
  }, [selectedGameIds, games, onSwapGames])

  const handleSwapGames = useCallback(() => {
    if (!swapEligible || !onSwapGames) {
      return
    }
    onSwapGames(swapEligible.gameAId, swapEligible.gameBId)
    setSelectedGameIds(new Set())
  }, [swapEligible, onSwapGames])

  // Merge initial violations and lastEvaluation violations into a unified list.
  // Exact duplicates are removed so the inline count + expansion stay honest
  // (defensive against the solver's per-constraint double-emit paths).
  const mergedViolations = useMemo<InlineViolation[]>(() => {
    // If we have a post-edit evaluation, always use its violations (even if empty = all resolved)
    if (lastEvaluation) {
      const evalViolations = lastEvaluation.violations ?? []
      return evalViolations.map((v) => ({
        constraintName: v.constraint_name ?? v.constraint_type ?? 'Unknown',
        message: humanizeWeekRefs(v.message),
        suggestion: v.resolution_suggestion ?? null,
        severity: v.severity,
        outcome: readOutcome(v.context),
        affectedGameNumbers: v.affected_game_numbers ?? [],
        teamUids: v.affected_team_uids ?? [],
        isPrevented: v.is_prevented ?? false,
      }))
    }

    // Otherwise use initial violations from the scheduler request
    return dedupeViolations(initialViolations).map((v) => ({
      constraintName:
        v.constraint_type?.name ?? v.constraint_type?.code ?? 'Unknown',
      message: humanizeWeekRefs(v.message),
      suggestion: v.resolution_suggestion,
      severity: v.severity,
      outcome: readOutcome(v.context),
      affectedGameNumbers: v.affected_game_numbers ?? [],
      teamUids: v.affected_team_uids ?? [],
      isPrevented: v.is_prevented,
    }))
  }, [lastEvaluation, initialViolations])

  // game_number → its (over-broad) violated-constraint entries. Each row filters
  // these down to its own two teams before display (relatedGameViolations).
  const gameViolationsMap = useMemo(
    () => buildInlineGameViolations(mergedViolations),
    [mergedViolations]
  )

  // At-a-glance header badge counts, under the shared count policy. Forced
  // shortfalls / setup impossibilities are tracked separately — they are NOT
  // conflicts/advisories, but a clean strict win that has any must read
  // "Limited by setup", not "All hard rules satisfied" (invariant 4).
  const violationCounts = useMemo(() => {
    let conflicts = 0
    let advisories = 0
    let setupLimited = 0
    for (const v of mergedViolations) {
      if (isSetupLimitedOutcome(v.outcome)) {
        setupLimited++
        continue
      }
      const cls = classifyViolation(v.severity, v.isPrevented)
      if (cls === 'conflict') {
        conflicts++
      } else if (cls === 'advisory') {
        advisories++
      }
    }
    return { conflicts, advisories, setupLimited }
  }, [mergedViolations])

  // ─── Sorted games for table view ──────────────────────────────
  const sortedGames = useMemo(() => {
    if (!sortKey) {
      return games
    }
    const sorted = [...games]
    const dir = sortDirection === 'asc' ? 1 : -1

    sorted.sort((a, b) => {
      // Numeric sort for the game number — by the flight-unique global number
      // (the displayed value), falling back to the per-division number.
      if (sortKey === 'gameNumber') {
        return (
          ((a.globalGameNumber ?? a.gameNumber) -
            (b.globalGameNumber ?? b.gameNumber)) *
          dir
        )
      }

      let aVal: string
      let bVal: string

      switch (sortKey) {
        case 'date':
          aVal = a.startsAt ?? a.date
          bVal = b.startsAt ?? b.date
          break
        case 'time':
          aVal = a.time
          bVal = b.time
          break
        case 'homeTeam':
          aVal = a.homeTeam.toLowerCase()
          bVal = b.homeTeam.toLowerCase()
          break
        case 'awayTeam':
          aVal = a.awayTeam.toLowerCase()
          bVal = b.awayTeam.toLowerCase()
          break
        case 'location':
          aVal = a.location.toLowerCase()
          bVal = b.location.toLowerCase()
          break
        case 'surface':
          aVal = a.surface.toLowerCase()
          bVal = b.surface.toLowerCase()
          break
        case 'division':
          aVal = a.division.toLowerCase()
          bVal = b.division.toLowerCase()
          break
        default:
          return 0
      }

      if (aVal < bVal) {
        return -1 * dir
      }
      if (aVal > bVal) {
        return 1 * dir
      }
      return 0
    })

    return sorted
  }, [games, sortKey, sortDirection])

  const toggleViolationExpand = useCallback((gameNumber: number) => {
    setExpandedViolationGames((prev) => {
      const next = new Set(prev)
      if (next.has(gameNumber)) {
        next.delete(gameNumber)
      } else {
        next.add(gameNumber)
      }
      return next
    })
  }, [])

  // Local edit form state
  const [editForm, setEditForm] = useState<EditFormState>({
    venueId: '',
    facilityId: '',
    homeTeamId: '',
    awayTeamId: '',
    startsAt: '',
    endsAt: '',
  })

  // Build team options per division
  const teamsByDivision = useMemo(() => {
    const map = new Map<string, TeamOption[]>()
    for (const div of divisions) {
      if (div.teams) {
        map.set(
          div.id,
          div.teams.map((t: { id: string; name: string }) => ({
            id: t.id,
            name: t.name,
          }))
        )
      }
    }
    return map
  }, [divisions])

  // Facilities for the selected venue in edit mode
  const facilitiesForSelectedVenue = useMemo(() => {
    if (!editForm.venueId) {
      return []
    }
    const venue = venues.find((v) => v.id === editForm.venueId)
    return venue?.facilities ?? []
  }, [editForm.venueId, venues])

  // ─── Date/Time derivations ─────────────────────────────────────

  const minDate = useMemo(
    () =>
      competition?.starts_at
        ? dayjs.utc(competition.starts_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.starts_at]
  )

  const maxDate = useMemo(
    () =>
      competition?.ends_at
        ? dayjs.utc(competition.ends_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.ends_at]
  )

  // The selected date from editForm.startsAt (YYYY-MM-DD portion)
  const selectedDate = useMemo(
    () => (editForm.startsAt ? editForm.startsAt.slice(0, 10) : ''),
    [editForm.startsAt]
  )

  // Time ranges available for the selected facility + day-of-week
  const availableTimeRanges = useMemo(() => {
    if (!editForm.facilityId || !selectedDate) {
      return []
    }
    const dow = dayjs.utc(selectedDate).day()
    return getTimeRangesForFacilityAndDay(timeSlots, editForm.facilityId, dow)
  }, [timeSlots, editForm.facilityId, selectedDate])

  const availableHourSlots = useMemo(
    () => splitIntoHourSlots(availableTimeRanges),
    [availableTimeRanges]
  )

  // Days of week that have at least one time range for the selected facility
  const availableDaysOfWeek = useMemo(() => {
    if (!editForm.facilityId) {
      return new Set<number>()
    }
    const days = new Set<number>()
    for (const ts of timeSlots) {
      if (!ts.is_active || ts.facility_id !== editForm.facilityId) {
        continue
      }
      if (ts.time_ranges.length === 0) {
        continue
      }
      for (const d of ts.days) {
        days.add(d)
      }
    }
    return days
  }, [timeSlots, editForm.facilityId])

  // Blackout dates (YYYY-MM-DD strings) that apply to the selected venue
  const blackoutDateSet = useMemo(() => {
    const dates = new Set<string>()
    for (const c of constraints) {
      if (c.type.code !== 'venue_blackout' || !c.is_enabled) {
        continue
      }
      const params = c.parameters as {
        dates?: string[]
        venue_uids?: string[]
      }
      // If venue_uids is set, only apply when the selected venue matches
      if (
        params.venue_uids &&
        params.venue_uids.length > 0 &&
        editForm.venueId &&
        !params.venue_uids.includes(editForm.venueId)
      ) {
        continue
      }
      for (const d of params.dates || []) {
        dates.add(d)
      }
    }
    return dates
  }, [constraints, editForm.venueId])

  // Disable dates outside competition range, without time slots, or on blackout
  const isDateDisabled = useCallback(
    (current: Dayjs) => {
      if (minDate && current.isBefore(dayjs.utc(minDate), 'day')) {
        return true
      }
      if (maxDate && current.isAfter(dayjs.utc(maxDate), 'day')) {
        return true
      }
      // Blackout dates
      if (blackoutDateSet.has(current.format('YYYY-MM-DD'))) {
        return true
      }
      // If a facility is selected, disable days of week without slots
      if (editForm.facilityId && availableDaysOfWeek.size > 0) {
        return !availableDaysOfWeek.has(current.day())
      }
      return false
    },
    [
      minDate,
      maxDate,
      editForm.facilityId,
      availableDaysOfWeek,
      blackoutDateSet,
    ]
  )

  // Currently selected time value (HH:mm from startsAt)
  const selectedTime = useMemo(() => {
    if (!editForm.startsAt || editForm.startsAt.length < 16) {
      return ''
    }
    return editForm.startsAt.slice(11, 16)
  }, [editForm.startsAt])

  // Dropdown options: availableHourSlots + the game's current slot if not already listed
  const timeSlotOptions = useMemo(() => {
    if (
      !selectedTime ||
      availableHourSlots.some((s) => s.start === selectedTime)
    ) {
      return availableHourSlots
    }
    const endTime =
      editForm.endsAt?.length >= 16
        ? editForm.endsAt.slice(11, 16)
        : selectedTime
    const currentSlot: HourSlot = { start: selectedTime, end: endTime }
    return [...availableHourSlots, currentSlot].sort((a, b) =>
      a.start.localeCompare(b.start)
    )
  }, [availableHourSlots, selectedTime, editForm.endsAt])

  const handleRowClick = useCallback(
    (game: GeneratedGame) => {
      // Any row interaction dismisses the jump-to-game highlight — it has served
      // its "here's where you landed" purpose.
      setFlashedGameId(null)
      if (editingGameId === game.id) {
        return
      }
      if (!onStartEditing) {
        return
      }

      onStartEditing(game.id)
      setEditForm({
        venueId: game.locationId ?? '',
        facilityId: game.surfaceId ?? '',
        homeTeamId: game.homeTeamId ?? '',
        awayTeamId: game.awayTeamId ?? '',
        startsAt: game.startsAt ?? '',
        endsAt: game.endsAt ?? '',
      })
    },
    [editingGameId, onStartEditing]
  )

  const handleSave = useCallback(
    async (gameId: string) => {
      if (!onSaveGame) {
        return
      }

      const originalGame = games.find((g) => g.id === gameId)
      if (!originalGame) {
        return
      }

      const data = buildEditDiff(editForm, originalGame)
      if (!data) {
        onCancelEditing?.()
        return
      }

      const result = await onSaveGame(gameId, data as GameEditData)
      if (result) {
        onCancelEditing?.()
      }
    },
    [onSaveGame, onCancelEditing, editForm, games]
  )

  const handleCancel = useCallback(() => {
    onCancelEditing?.()
    setEditForm({
      venueId: '',
      facilityId: '',
      homeTeamId: '',
      awayTeamId: '',
      startsAt: '',
      endsAt: '',
    })
  }, [onCancelEditing])

  // Click-outside to cancel editing
  const editingRowRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!editingGameId) {
      return
    }
    const handleMouseDown = (e: MouseEvent) => {
      if (
        editingRowRef.current &&
        !editingRowRef.current.contains(e.target as Node)
      ) {
        handleCancel()
      }
    }
    document.addEventListener('mousedown', handleMouseDown)
    return () => document.removeEventListener('mousedown', handleMouseDown)
  }, [editingGameId, handleCancel])

  // ─── Jump-to-game: scroll the row into view + briefly flash it ──────────────
  const containerRef = useRef<HTMLDivElement>(null)
  const [flashedGameId, setFlashedGameId] = useState<string | null>(null)
  useEffect(() => {
    if (highlightGameId == null) {
      return
    }
    const el = containerRef.current?.querySelector(
      `[data-game-id="${highlightGameId}"]`
    )
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setFlashedGameId(highlightGameId)
    const timer = window.setTimeout(() => setFlashedGameId(null), 5000)
    return () => window.clearTimeout(timer)
    // highlightToken changes on every jump so repeat jumps to the same game re-fire.
  }, [highlightGameId, highlightToken])

  const getScoreColor = (score: number | null | undefined) => {
    if (score == null) {
      return colors.NEUTRAL_50
    }
    if (score >= 90) {
      return colors.SUCCESS_300
    }
    return colors.WARNING_300
  }

  const getScoreBg = (score: number | null | undefined) => {
    if (score == null) {
      return `${colors.NEUTRAL_50}20`
    }
    if (score >= 90) {
      return `${colors.SUCCESS_300}20`
    }
    return `${colors.WARNING_300}20`
  }

  return (
    <div css={containerStyles} ref={containerRef}>
      {/* Section Header */}
      <div css={headerRowStyles}>
        <div css={sectionTitleContainerStyles}>
          <p css={sectionTitleStyles}>GENERATED GAMES</p>
          {satisfactionScore != null && (
            <span
              css={[
                scoreBadgeStyles,
                css`
                  color: ${getScoreColor(satisfactionScore)};
                  background: ${getScoreBg(satisfactionScore)};
                `,
              ]}
            >
              Score: {satisfactionScore.toFixed(1)}%
            </span>
          )}
          {/* Strict-first success chip. Understated, success-only: shown only
              when the parent reports every division satisfied all rules as hard
              AND this component renders zero inline violations of its own. This
              avoids claiming "All rules satisfied" over a schedule that has
              visible violations (e.g. the strict-skipped path). */}
          {constraintsStrictlySatisfied &&
            violationCounts.conflicts === 0 &&
            violationCounts.advisories === 0 &&
            violationCounts.setupLimited === 0 && (
              <Tooltip title='Every enabled rule was satisfied as a hard constraint — zero violations.'>
                <span
                  css={[
                    scoreBadgeStyles,
                    css`
                      color: ${colors.SUCCESS_300};
                      background: ${colors.SUCCESS_300}20;
                    `,
                  ]}
                >
                  All hard rules satisfied
                </span>
              </Tooltip>
            )}
          {/* A strict win can still have a forced shortfall — an aggregate target
              made unreachable by stronger hard rules. Show it calmly as "Limited
              by setup" rather than overclaiming "All hard rules satisfied"
              (invariant 4). */}
          {constraintsStrictlySatisfied &&
            violationCounts.conflicts === 0 &&
            violationCounts.advisories === 0 &&
            violationCounts.setupLimited > 0 && (
              <Tooltip title='Every enabled hard rule was satisfied, but stronger rules made some targets unreachable — those are explained as "Limited by setup", not conflicts.'>
                <span
                  css={[
                    scoreBadgeStyles,
                    css`
                      color: ${colors.INFO_300};
                      background: ${colors.INFO_300}20;
                    `,
                  ]}
                >
                  Limited by setup
                </span>
              </Tooltip>
            )}
          {violationCounts.conflicts > 0 && (
            <Tooltip
              title={`${violationCounts.conflicts} conflict${
                violationCounts.conflicts === 1 ? '' : 's'
              } flagged on games below`}
            >
              <span
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 20px;
                  height: 20px;
                  padding: 0 6px;
                  border-radius: 10px;
                  background-color: ${colors.ERROR_200};
                  font-family: 'Inter', sans-serif;
                  font-weight: 600;
                  font-size: 12px;
                  color: ${colors.WHITE};
                  margin-left: 4px;
                `}
              >
                {violationCounts.conflicts}
              </span>
            </Tooltip>
          )}
          {violationCounts.advisories > 0 && (
            // Advisories live in the constraint panel — this badge points there
            // rather than expanding anything inline.
            <Tooltip title='View violations in the Constraints & Violations panel'>
              <button
                type='button'
                onClick={onShowAdvisories}
                disabled={!onShowAdvisories}
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 20px;
                  height: 20px;
                  padding: 0 6px;
                  border: none;
                  border-radius: 10px;
                  background-color: ${colors.WARNING_300};
                  font-family: 'Inter', sans-serif;
                  font-weight: 600;
                  font-size: 12px;
                  color: ${colors.SECONDARY_500};
                  margin-left: 4px;
                  cursor: ${onShowAdvisories ? 'pointer' : 'default'};
                `}
              >
                {violationCounts.advisories}
              </button>
            </Tooltip>
          )}
        </div>
        <div
          css={css`
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          {onSwapGames && selectedGameIds.size === 2 && (
            <Tooltip
              title={
                swapEligible
                  ? 'Swap these two games\u2019 time slots'
                  : 'Select 2 games from the same division to swap'
              }
            >
              <button
                type='button'
                onClick={handleSwapGames}
                disabled={!swapEligible}
                css={css`
                  ${exportButtonStyles};
                  background: ${swapEligible
                    ? colors.PRIMARY
                    : colors.SECONDARY_300};
                  border-color: ${swapEligible
                    ? colors.PRIMARY
                    : colors.SOFT_STEEL};
                  opacity: ${swapEligible ? 1 : 0.5};
                  cursor: ${swapEligible ? 'pointer' : 'not-allowed'};
                  &:hover {
                    background: ${swapEligible
                      ? colors.PRIMARY_DARK
                      : colors.SECONDARY_300};
                  }
                `}
              >
                <p
                  css={css`
                    ${exportButtonTextStyles};
                    color: ${swapEligible
                      ? colors.SECONDARY_300
                      : colors.NEUTRAL_50};
                  `}
                >
                  Swap Games
                </p>
              </button>
            </Tooltip>
          )}
          {onSaveCheckpoint && (
            <button
              type='button'
              onClick={onSaveCheckpoint}
              disabled={isSavingCheckpoint || isRestoringCheckpoint}
              css={css`
                ${exportButtonStyles};
                background: transparent;
                border-color: ${colors.PRIMARY};
                &:hover {
                  background: ${colors.PRIMARY}15;
                }
                &:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                }
              `}
            >
              <p
                css={css`
                  ${exportButtonTextStyles};
                  color: ${colors.PRIMARY};
                `}
              >
                {isSavingCheckpoint ? 'Saving...' : 'Save'}
              </p>
            </button>
          )}
          {onRestoreCheckpoint && hasChanges && (
            <button
              type='button'
              onClick={onRestoreCheckpoint}
              disabled={isSavingCheckpoint || isRestoringCheckpoint}
              css={css`
                ${exportButtonStyles};
                background: transparent;
                border-color: ${colors.ERROR_300};
                &:hover {
                  background: ${colors.ERROR_300}15;
                }
                &:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                }
              `}
            >
              <p
                css={css`
                  ${exportButtonTextStyles};
                  color: ${colors.ERROR_300};
                `}
              >
                {isRestoringCheckpoint ? 'Undoing...' : 'Undo'}
              </p>
            </button>
          )}
          <button type='button' onClick={onExport} css={exportButtonStyles}>
            <DownloadIcon size={24} color={colors.PRIMARY} />
            <p css={exportButtonTextStyles}>Export</p>
          </button>
        </div>
      </div>

      {/* Filters (injected from parent) */}
      {expanded && filterContent}

      {/* Content: Table */}
      {expanded && (
        <div css={tableContainerStyles}>
          {/* Error banner */}
          {editError && (
            <div css={errorBannerStyles}>
              <span>{editError}</span>
              <button
                type='button'
                css={iconBtnStyles}
                onClick={onCancelEditing}
                aria-label='Dismiss error'
              >
                <CloseIcon size={16} color={colors.ERROR_100} />
              </button>
            </div>
          )}

          {/* Table Header */}
          <div css={tableHeaderStyles}>
            <p
              css={[
                sortableHeaderStyles,
                colGameNum,
                sortKey === 'gameNumber' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('gameNumber')}
            >
              GAME #
              <SortIndicator
                active={sortKey === 'gameNumber'}
                direction={sortKey === 'gameNumber' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colDate,
                sortKey === 'date' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('date')}
            >
              DATE
              <SortIndicator
                active={sortKey === 'date'}
                direction={sortKey === 'date' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colTime,
                sortKey === 'time' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('time')}
            >
              TIME
              <SortIndicator
                active={sortKey === 'time'}
                direction={sortKey === 'time' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colFlex,
                sortKey === 'homeTeam' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('homeTeam')}
            >
              HOME
              <SortIndicator
                active={sortKey === 'homeTeam'}
                direction={sortKey === 'homeTeam' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colFlex,
                sortKey === 'awayTeam' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('awayTeam')}
            >
              AWAY
              <SortIndicator
                active={sortKey === 'awayTeam'}
                direction={sortKey === 'awayTeam' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colLocation,
                sortKey === 'location' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('location')}
            >
              LOCATION
              <SortIndicator
                active={sortKey === 'location'}
                direction={sortKey === 'location' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colSurface,
                sortKey === 'surface' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('surface')}
            >
              SURFACE
              <SortIndicator
                active={sortKey === 'surface'}
                direction={sortKey === 'surface' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                sortableHeaderStyles,
                colFlex,
                sortKey === 'division' && activeSortHeaderStyles,
              ]}
              onClick={() => handleSort('division')}
            >
              DIVISION
              <SortIndicator
                active={sortKey === 'division'}
                direction={sortKey === 'division' ? sortDirection : 'asc'}
              />
            </p>
            <p
              css={[
                columnHeaderStyles,
                css`
                  width: 128px;
                  flex-shrink: 0;
                `,
              ]}
            >
              STATUS
            </p>
          </div>

          {/* Empty state */}
          {games.length === 0 && !loading && (
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 32px 16px;
                font-family: 'Inter', sans-serif;
                font-size: 14px;
                color: ${colors.NEUTRAL_50};
              `}
            >
              No games generated yet.
            </div>
          )}

          {/* Data Rows */}
          {sortedGames.map((game, index) => {
            const isEven = index % 2 === 0
            const isEditing = editingGameId === game.id
            const divisionTeams = teamsByDivision.get(game.divisionId) ?? []
            const gameNumber = game.gameNumber
            // Show only the violations that actually involve THIS game's two
            // teams — the solver's affected_game_numbers is over-broad, so an
            // unfiltered row would list advisories about teams not even playing.
            const rowViolations = relatedGameViolations(
              gameViolationsMap.get(gameNumber) ?? [],
              game.homeTeamId,
              game.awayTeamId
            )
            // Conflicts (red) highlight + expand; advisory-only rows (no
            // conflict) wear the calm yellow marker. Both expand to the detail.
            const rowHasConflict = rowViolations.some(
              (v) => v.severity === 'error'
            )
            const isAffected = rowHasConflict
            const isAdvisory = rowViolations.length > 0 && !rowHasConflict
            const isViolationExpanded = expandedViolationGames.has(gameNumber)
            const conflictTooltip = `${rowViolations.length} conflict${
              rowViolations.length === 1 ? '' : 's'
            } — click to resolve`
            const advisoryTooltip = `${rowViolations.length} violation${
              rowViolations.length === 1 ? '' : 's'
            } — click for details`

            if (isEditing) {
              // ─── Edit Mode Row ───
              return (
                <div
                  ref={editingRowRef}
                  key={game.id}
                  css={css`
                    display: flex;
                    gap: 16px;
                    align-items: center;
                    padding: 8px 16px;
                    background: ${colors.SECONDARY_200};
                    border-left: 3px solid ${colors.PRIMARY};
                  `}
                >
                  {/* Game number (read-only, flight-unique) */}
                  <p css={[cellTextStyles, colGameNum]}>
                    {game.globalGameNumber ?? game.gameNumber}
                  </p>

                  {/* Date picker */}
                  <div css={colDate}>
                    <DatePicker
                      css={inlineDatePickerStyles}
                      value={selectedDate ? dayjs.utc(selectedDate) : null}
                      format='YYYY-MM-DD'
                      disabledDate={isDateDisabled}
                      allowClear={false}
                      getPopupContainer={(trigger) =>
                        trigger.parentNode as HTMLElement
                      }
                      onChange={(date: Dayjs | null) => {
                        if (!date) {
                          return
                        }
                        const newDate = date.format('YYYY-MM-DD')
                        // Preserve existing time portion, or default to T00:00:00
                        const timePart =
                          editForm.startsAt?.slice(10) || 'T00:00:00'
                        setEditForm((prev) => ({
                          ...prev,
                          startsAt: newDate + timePart,
                          // Clear endsAt time — available ranges depend on day-of-week
                          endsAt: '',
                        }))
                      }}
                    />
                  </div>

                  {/* Time slot dropdown — 1-hour slots within available ranges */}
                  <div css={colTime}>
                    <select
                      css={inlineSelectStyles}
                      value={selectedTime}
                      disabled={!selectedDate || timeSlotOptions.length === 0}
                      onChange={(e) => {
                        const val = e.target.value
                        if (!val) {
                          return
                        }
                        const slot = timeSlotOptions.find(
                          (s) => s.start === val
                        )
                        if (!slot) {
                          return
                        }
                        const datePart = selectedDate
                        setEditForm((prev) => ({
                          ...prev,
                          startsAt: `${datePart}T${slot.start}:00`,
                          endsAt: `${datePart}T${slot.end}:00`,
                        }))
                      }}
                    >
                      <option value=''>
                        {!selectedDate
                          ? 'Pick date first'
                          : timeSlotOptions.length === 0
                          ? 'No slots'
                          : 'Select Time'}
                      </option>
                      {timeSlotOptions.map((s) => (
                        <option key={`${s.start}-${s.end}`} value={s.start}>
                          {s.start} - {s.end}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Home Team dropdown */}
                  <div css={colFlex}>
                    <select
                      css={inlineSelectStyles}
                      value={editForm.homeTeamId}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          homeTeamId: e.target.value,
                        }))
                      }
                    >
                      <option value=''>Select Home Team</option>
                      {divisionTeams
                        .filter((t) => t.id !== editForm.awayTeamId)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Away Team dropdown */}
                  <div css={colFlex}>
                    <select
                      css={inlineSelectStyles}
                      value={editForm.awayTeamId}
                      onChange={(e) =>
                        setEditForm((prev) => ({
                          ...prev,
                          awayTeamId: e.target.value,
                        }))
                      }
                    >
                      <option value=''>Select Away Team</option>
                      {divisionTeams
                        .filter((t) => t.id !== editForm.homeTeamId)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Location dropdown */}
                  <div css={colLocation}>
                    <select
                      css={inlineSelectStyles}
                      value={editForm.venueId}
                      onChange={(e) => {
                        const datePart = editForm.startsAt
                          ? editForm.startsAt.slice(0, 10)
                          : ''
                        setEditForm((prev) => ({
                          ...prev,
                          venueId: e.target.value,
                          facilityId: '', // Reset facility on venue change
                          // Clear time — time slots are facility-specific
                          startsAt: datePart
                            ? `${datePart}T00:00:00`
                            : prev.startsAt,
                          endsAt: '',
                        }))
                      }}
                    >
                      <option value=''>Select Location</option>
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Surface dropdown (cascading from venue) */}
                  <div css={colSurface}>
                    <select
                      css={inlineSelectStyles}
                      value={editForm.facilityId}
                      onChange={(e) => {
                        const datePart = editForm.startsAt
                          ? editForm.startsAt.slice(0, 10)
                          : ''
                        setEditForm((prev) => ({
                          ...prev,
                          facilityId: e.target.value,
                          // Clear time — time ranges depend on facility
                          startsAt: datePart
                            ? `${datePart}T00:00:00`
                            : prev.startsAt,
                          endsAt: '',
                        }))
                      }}
                      disabled={!editForm.venueId}
                    >
                      <option value=''>Select Surface</option>
                      {facilitiesForSelectedVenue.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Division (read-only) */}
                  <p css={[cellTextStyles, colFlex]}>{game.division}</p>

                  {/* Save / Cancel */}
                  <div
                    css={css`
                      width: 128px;
                      flex-shrink: 0;
                      display: flex;
                      gap: 6px;
                    `}
                  >
                    <button
                      type='button'
                      disabled={saving}
                      onClick={() => handleSave(game.id)}
                      css={[
                        editActionBtnStyles,
                        css`
                          background: ${colors.PRIMARY};
                          color: ${colors.PRIMARY_TEXT};
                          &:disabled {
                            opacity: 0.5;
                            cursor: not-allowed;
                          }
                        `,
                      ]}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      type='button'
                      disabled={saving}
                      onClick={handleCancel}
                      css={[
                        editActionBtnStyles,
                        css`
                          background: transparent;
                          color: ${colors.NEUTRAL_50};
                          border: 1px solid ${colors.SECONDARY_100};
                        `,
                      ]}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )
            }

            // ─── Read Mode Row ───
            return (
              <React.Fragment key={game.id}>
                <div
                  data-game-id={game.id}
                  data-game-number={gameNumber}
                  css={getRowStyles(
                    isEven,
                    false,
                    isAffected,
                    publishedDivisionIds?.has(game.divisionId),
                    flashedGameId === game.id
                  )}
                  onClick={() => handleRowClick(game)}
                  role='button'
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      handleRowClick(game)
                    }
                  }}
                >
                  <p css={[cellTextStyles, colGameNum]}>
                    {game.globalGameNumber ?? game.gameNumber}
                  </p>
                  <p
                    css={[
                      cellTextStyles,
                      colDate,
                      css`
                        height: 20px;
                      `,
                    ]}
                  >
                    {game.date}
                  </p>
                  <p css={[cellTextStyles, colTime]}>{game.time}</p>
                  <p css={[cellTextStyles, colFlex]}>{game.homeTeam}</p>
                  <p css={[cellTextStyles, colFlex]}>{game.awayTeam}</p>
                  <p css={[cellTextStyles, colLocation]}>{game.location}</p>
                  <p css={[cellTextStyles, colSurface]}>{game.surface}</p>
                  <p css={[cellTextStyles, colFlex]}>{game.division}</p>
                  <div css={statusCellStyles}>
                    {isAffected ? (
                      // CONFLICT — red, expandable inline detail.
                      <Tooltip title={conflictTooltip} placement='top'>
                        <button
                          type='button'
                          css={iconBtnStyles}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (rowViolations.length > 0) {
                              toggleViolationExpand(gameNumber)
                            }
                          }}
                        >
                          <WarningIcon size={20} color={colors.ERROR_200} />
                          {rowViolations.length > 0 && (
                            <ExpandMoreIcon
                              size={16}
                              color={colors.ERROR_200}
                              expanded={isViolationExpanded}
                            />
                          )}
                        </button>
                      </Tooltip>
                    ) : isAdvisory ? (
                      // ADVISORY — calm yellow icon; expand to reveal the
                      // violated constraint inline (detail also lives in the panel).
                      <Tooltip title={advisoryTooltip} placement='top'>
                        <button
                          type='button'
                          css={iconBtnStyles}
                          onClick={(e) => {
                            e.stopPropagation()
                            if (rowViolations.length > 0) {
                              toggleViolationExpand(gameNumber)
                            }
                          }}
                        >
                          <WarningIcon size={20} color={colors.WARNING_200} />
                          {rowViolations.length > 0 && (
                            <ExpandMoreIcon
                              size={16}
                              color={colors.WARNING_200}
                              expanded={isViolationExpanded}
                            />
                          )}
                        </button>
                      </Tooltip>
                    ) : game.hasWarning ? (
                      <Tooltip title='Has warnings' placement='top'>
                        <span css={tooltipIconWrapper}>
                          <WarningIcon size={20} color={colors.WARNING_200} />
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip
                        title='Valid — no issues detected'
                        placement='top'
                      >
                        <span css={tooltipIconWrapper}>
                          <SuccessIcon size={20} color={colors.SUCCESS_300} />
                        </span>
                      </Tooltip>
                    )}
                  </div>
                </div>
                {/* Inline violation sub-rows */}
                {isViolationExpanded && rowViolations.length > 0 && (
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      border-left: 3px solid
                        ${rowViolations.some((v) => v.severity === 'error')
                          ? colors.ERROR_200
                          : colors.WARNING_300};
                      background: ${rowViolations.some(
                        (v) => v.severity === 'error'
                      )
                        ? 'rgba(223, 66, 66, 0.06)'
                        : 'rgba(255, 193, 7, 0.06)'};
                    `}
                  >
                    {rowViolations.map((v, vi) => (
                      <div
                        key={vi}
                        css={css`
                          display: flex;
                          align-items: flex-start;
                          gap: 12px;
                          padding: 8px 16px 8px 20px;
                          border-bottom: 1px solid ${colors.SECONDARY_200}40;
                          &:last-of-type {
                            border-bottom: none;
                          }
                        `}
                      >
                        <span
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            font-weight: 600;
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            color: ${isSetupLimitedOutcome(v.outcome)
                              ? colors.NEUTRAL_50
                              : v.severity === 'error'
                              ? colors.ERROR_200
                              : colors.WARNING_300};
                            background: ${isSetupLimitedOutcome(v.outcome)
                              ? `${colors.NEUTRAL_50}20`
                              : v.severity === 'error'
                              ? `${colors.ERROR_200}20`
                              : `${colors.WARNING_300}20`};
                            padding: 2px 6px;
                            border-radius: 4px;
                            white-space: nowrap;
                            flex-shrink: 0;
                            margin-top: 1px;
                          `}
                        >
                          {outcomeAwareLabel(v.severity, v.outcome)}
                        </span>
                        <span
                          css={css`
                            font-family: 'Inter', sans-serif;
                            font-size: 12px;
                            font-weight: 500;
                            color: ${colors.NEUTRAL_70};
                            text-transform: uppercase;
                            letter-spacing: 0.5px;
                            flex-shrink: 0;
                            width: 130px;
                            margin-top: 2px;
                          `}
                        >
                          {v.constraintName}
                        </span>
                        <div
                          css={css`
                            flex: 1;
                            min-width: 0;
                          `}
                        >
                          <p
                            css={css`
                              font-family: 'Inter', sans-serif;
                              font-size: 13px;
                              color: ${colors.WHITE};
                              margin: 0;
                              line-height: 18px;
                            `}
                          >
                            {intuitiveMessage(
                              v.message,
                              resolveTeamLabel(v.teamUids, teamNames)
                            )}
                          </p>
                          {v.suggestion && (
                            <p
                              css={css`
                                font-family: 'Inter', sans-serif;
                                font-size: 12px;
                                color: ${colors.NEUTRAL_50};
                                margin: 3px 0 0;
                                font-style: italic;
                                line-height: 17px;
                              `}
                            >
                              {v.suggestion}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default GeneratedGamesSection
